/**
 * Thin fetch wrapper that attaches the Cognito access token and parses the
 * { error: { code, message, requestId } } envelope returned by our Lambdas.
 */

import { getIdToken } from '@/lib/auth/cognito-client';

// API base. In CI builds VITE_API_URL is set to "/api" so requests are
// same-origin (browser → /api/* → CloudFront → API Gateway). In local dev
// you can override to a full URL like "https://dev.idscentral.com/api" or
// the raw API Gateway endpoint for direct testing.
const rawBaseUrl = import.meta.env.VITE_API_URL ?? '/api';
const baseUrl = rawBaseUrl.replace(/\/+$/, ''); // strip trailing slash

function buildUrl(path: string): string {
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  if (/^https?:\/\//.test(baseUrl)) {
    return `${baseUrl}${cleanPath}`;
  }
  // baseUrl is relative (e.g. "/api"). Resolve against the current origin.
  return `${baseUrl}${cleanPath}`;
}

export class ApiClientError extends Error {
  readonly code: string;
  readonly status: number;
  readonly requestId?: string;
  readonly details?: Record<string, unknown>;

  constructor(
    code: string,
    status: number,
    message: string,
    requestId?: string,
    details?: Record<string, unknown>
  ) {
    super(message);
    this.code = code;
    this.status = status;
    this.requestId = requestId;
    this.details = details;
  }
}

export interface ApiRequestOptions extends Omit<RequestInit, 'body'> {
  /** JSON body — will be stringified. */
  body?: unknown;
  /** Skip Authorization header (public endpoints like POST /waitlist). */
  unauthenticated?: boolean;
}

export async function api<T = unknown>(
  path: string,
  options: ApiRequestOptions = {}
): Promise<T> {
  const headers = new Headers(options.headers);
  if (!headers.has('Content-Type') && options.body !== undefined) {
    headers.set('Content-Type', 'application/json');
  }
  if (!options.unauthenticated) {
    const token = getIdToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }

  const init: RequestInit = {
    ...options,
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  };

  const url = buildUrl(path);
  const res = await fetch(url, init);

  if (res.status === 204) {
    return undefined as T;
  }

  let payload: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = { raw: text };
    }
  }

  if (!res.ok) {
    const envelope = (payload ?? {}) as {
      error?: {
        code?: string;
        message?: string;
        requestId?: string;
        details?: Record<string, unknown>;
      };
    };
    const err = envelope.error ?? {};
    throw new ApiClientError(
      err.code ?? `HTTP_${res.status}`,
      res.status,
      err.message ?? res.statusText,
      err.requestId,
      err.details
    );
  }

  return payload as T;
}
